import { convertParametersToGroup } from "@/input/utils/groupUngroup";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

import type { NodeModel } from "@/models/nodes/types";
import type { NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";

function isUnaryFunctionOperator(node: NodeModel) {
  return node.is(OperatorNodeModel) && node.placement === "function" && node.arity === "unary";
}

export class EditNodeVisitor implements NodeVisitor<void> {
  private updatedNode: NodeModel;

  constructor(node: NodeModel) {
    this.updatedNode = node;
  }

  private convertParametersToGroup(node: FunctionNodeModel | VariableNodeModel): void {
    const groupNode = convertParametersToGroup(node);
    this.updatedNode.insertSiblingAfter(groupNode);
  }

  visitGroupNode(node: NodeModel<"group">): void {
    throw new Error(`EditNodeVisitor is not implemented for ${node.type}`);
  }

  visitOperatorNode(node: NodeModel<"operator">): void {
    const { doc } = node;

    switch (node.placement) {
      case "function":
        if (node.arity === "unary") {
          const isInterchangeable = isUnaryFunctionOperator(this.updatedNode);

          /**
           * Check if the updated node has the same arity and placement.
           * This way we can create new operator based on the updated node's `operatorType`
           * and pass original node's child nodes to this operator.
           *
           * Ex. sin(x + y) => cos(x + y)
           */
          if (isInterchangeable) {
            /**
             * Create a new operator based on the updated node's `operatorType`
             * and pass original node's child nodes
             */
            const operator = doc.create(
              OperatorNodeModel,
              (this.updatedNode as NodeModel<"operator">).operatorType,
              node.parameters,
              undefined,
              (this.updatedNode as NodeModel<"operator">).props.name,
            );

            // Replace original node with newly created operator
            doc.cursor.moveBeforeNode(node);
            node.replaceWith(operator);
            doc.cursor.moveRight();
          } else if (node.parameters[0].nodes.length > 1) {
            const groupNode = doc.create(GroupNodeModel, node.parameters[0].nodes, "(", ")");
            node.insertSiblingBefore(this.updatedNode);
            node.insertSiblingBefore(groupNode);
            doc.removeNode(node);
            doc.cursor.moveTo(this.updatedNode.parent, this.updatedNode.index);
          } else {
            node.insertSiblingBefore(this.updatedNode);
            node.parameters[0].nodes.forEach((n) => {
              node.insertSiblingBefore(n);
            });
            doc.removeNode(node);
            doc.cursor.moveTo(this.updatedNode.parent, this.updatedNode.index);
          }
        } else {
          // Implement editing for operators with other arity
        }
        break;

      default:
        // Implement editing for operators with other placement
        break;
    }
  }

  visitOperatorSymbolNode(node: NodeModel<"operatorSymbol">): void {
    node.replaceWith(this.updatedNode);
  }

  visitLabelNode(node: NodeModel<"label">): void {
    throw new Error(`EditNodeVisitor is not implemented for ${node.type}`);
  }

  visitSymbolNode(node: NodeModel<"symbol">): void {
    node.replaceWith(this.updatedNode);
  }

  visitNumberNode(node: NodeModel<"number">): void {
    node.replaceWith(this.updatedNode);
  }

  visitVariableNode(node: NodeModel<"variable">): void {
    node.replaceWith(this.updatedNode);

    if (!node.paramsAreEmpty && !this.updatedNode.is(VariableNodeModel)) {
      this.convertParametersToGroup(node);
    }
  }

  visitTextNode(node: NodeModel<"text">): void {
    throw new Error(`EditNodeVisitor is not implemented for ${node.type}`);
  }

  visitConstantNode(node: NodeModel<"constant">): void {
    node.replaceWith(this.updatedNode);
  }

  visitFunctionNode(node: NodeModel<"function">): void {
    node.replaceWith(this.updatedNode);

    if (this.updatedNode.is(FunctionNodeModel)) {
      this.updatedNode.parenthesesVisibility = node.parenthesesVisibility;
      this.updatedNode.openingSymbol = node.openingSymbol;
      this.updatedNode.closingSymbol = node.closingSymbol;

      if (!node.paramsAreEmpty) {
        node.parameters.forEach((param, index) => {
          const updatedNode = this.updatedNode as FunctionNodeModel;

          if (updatedNode.parameters[index]) {
            updatedNode.parameters[index].replaceWith(param);
          } else {
            this.updatedNode.appendChild(param);
          }
        });
      }
    } else if (!node.paramsAreEmpty) {
      this.convertParametersToGroup(node);
    }
  }

  visitInlineNode(node: NodeModel<"inline">): void {
    throw new Error(`EditNodeVisitor is not implemented for ${node.type}`);
  }

  visitWrapperNode(node: NodeModel<"wrapper">): void {
    throw new Error(`EditNodeVisitor is not implemented for ${node.type}`);
  }

  visitPlainTextNode(node: NodeModel<"plainText">): void {
    node.replaceWith(this.updatedNode);
  }

  visitMatrixNode(node: NodeModel<"matrix">): void {
    throw new Error(`EditNodeVisitor is not implemented for ${node.type}`);
  }

  visitDecoratorNode(node: NodeModel<"decorator">) {
    throw new Error(`EditNodeVisitor is not implemented for ${node.type}`);
  }

  visitUnitNode(node: NodeModel<"unit">): void {
    throw new Error(`EditNodeVisitor is not implemented for ${node.type}`);
  }

  visitGraphNode(node: NodeModel<"graph">): void {
    throw new Error(`EditNodeVisitor is not implemented for ${node.type}`);
  }

  visitGraphExpressionNode(node: NodeModel<"graphExpression">): void {
    throw new Error(`EditNodeVisitor is not implemented for ${node.type}`);
  }

  visitPlaceholderNode(node: NodeModel<"placeholder">): void {
    throw new Error(`EditNodeVisitor is not implemented for ${node.type}`);
  }

  visitComputedReferenceNode(node: NodeModel<"computedReference">): void {
    throw new Error(`EditNodeVisitor is not implemented for ${node.type}`);
  }

  visitComputedResultNode(node: NodeModel<"computedResult">): void {
    throw new Error(`EditNodeVisitor is not implemented for ${node.type}`);
  }
}
